import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { z } from 'zod';

import { type OptionType } from '@/api/common';
import { type PixelType } from '@/api/pixel';
import { withForm } from '@/components/Form';
import { FieldDescription, FieldLabel } from '@/components/Form/fields';
import { SupportLink } from '@/components/typography';
import { maxObjectDescriptionLength, maxObjectNameLength } from '@/constants/numbers';
import { AudienceSelect, ChannelSelect } from '@/features/inputs';
import VendorSelect from '@/features/inputs/VendorSelect';

import PixelURLInput from './PixelURLInput';

export type PixelFormValues = {
  name: string;
  description: string;
  audience: OptionType | null;
  channel: OptionType | null;
  vendor: OptionType | null;
  code?: string;
};

export const getPixelFormSchema = () => {
  const requiredOption = (message: string) =>
    z.custom<OptionType | null>().refine((value) => value != null, message);

  return z.object({
    name: z
      .string()
      .min(1, t`Name is required`)
      .max(maxObjectNameLength, t`Name must be less than ${maxObjectNameLength} characters`),
    description: z
      .string()
      .max(
        maxObjectDescriptionLength,
        t`Description must be less than ${maxObjectDescriptionLength} characters`,
      ),
    audience: requiredOption(t`Audience is required`),
    channel: requiredOption(t`Channel is required`),
    vendor: requiredOption(t`Vendor is required`),
    code: z.string().optional(),
  });
};

export const getPixelFormDefaultValues = (pixel?: PixelType): PixelFormValues => ({
  name: pixel?.name ?? '',
  description: pixel?.description ?? '',
  audience: pixel?.audience ?? null,
  channel: pixel?.channel ?? null,
  vendor: pixel?.vendor ?? null,
  code: pixel?.code,
});

const PixelForm = withForm({
  defaultValues: getPixelFormDefaultValues(),
  props: {
    pixel: undefined as PixelType | undefined,
    disabled: false,
    isEdit: false,
  },
  render: function Render({ form, pixel, disabled = false, isEdit = false }) {
    return (
      <div className="flex w-full flex-col gap-6">
        <div className="grid grid-cols-24 gap-4">
          <div className="col-span-24 lg:col-span-7">
            <form.AppField name="name">
              {(field) => (
                <field.Field>
                  <FieldLabel htmlFor="pixel-name">
                    <Trans>Pixel Name</Trans>
                  </FieldLabel>
                  <field.TextInput
                    id="pixel-name"
                    placeholder={t`Name`}
                    maxLength={maxObjectNameLength}
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
          <div className="col-span-24 lg:col-span-17">
            <form.AppField name="description">
              {(field) => (
                <field.Field>
                  <FieldLabel htmlFor="pixel-description">
                    <Trans>Pixel Description</Trans>
                  </FieldLabel>
                  <field.TextInput
                    id="pixel-description"
                    placeholder={t`Description`}
                    maxLength={maxObjectDescriptionLength}
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
          <div className="col-span-24 md:col-span-8">
            <form.AppField name="channel">
              {(field) => (
                <field.Field>
                  <FieldLabel htmlFor="pixel-channel">
                    <Trans>Channel</Trans>
                  </FieldLabel>
                  <ChannelSelect
                    id="pixel-channel"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(newChannel) => {
                      field.handleChange(newChannel);

                      const isChangingBackToSavedChannel = pixel?.channel.id === newChannel?.id;
                      form.setFieldValue(
                        'vendor',
                        isChangingBackToSavedChannel ? (pixel?.vendor ?? null) : null,
                      );
                    }}
                    disabled={isEdit || disabled}
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
          <div className="col-span-24 md:col-span-8">
            <form.Subscribe selector={(state) => state.values.channel}>
              {(channel) => (
                <form.AppField name="vendor">
                  {(field) => (
                    <field.Field>
                      <FieldLabel htmlFor="pixel-vendor">
                        <Trans>Vendor</Trans>
                      </FieldLabel>
                      <VendorSelect
                        id="pixel-vendor"
                        value={field.state.value}
                        onChange={field.handleChange}
                        onBlur={field.handleBlur}
                        channelId={channel?.id}
                        disabled={!channel?.id || isEdit || disabled}
                      />
                      <field.Error />
                    </field.Field>
                  )}
                </form.AppField>
              )}
            </form.Subscribe>
          </div>
          <div className="col-span-24 md:col-span-8">
            <form.AppField name="audience">
              {(field) => (
                <field.Field>
                  <FieldLabel htmlFor="pixel-audience">
                    <Trans>Audience</Trans>
                  </FieldLabel>
                  <AudienceSelect
                    id="pixel-audience"
                    value={field.state.value ?? undefined}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={disabled}
                    className="w-full"
                  />
                  <field.Error />
                </field.Field>
              )}
            </form.AppField>
          </div>
          <form.Subscribe
            selector={(state) => ({
              vendorId: state.values.vendor?.id,
              audienceId: state.values.audience?.id,
            })}
          >
            {({ vendorId, audienceId }) =>
              vendorId &&
              audienceId && (
                <>
                  <div className="col-span-24 xl:col-span-16 2xl:col-span-14">
                    <form.AppField name="code">
                      {(field) => (
                        <field.Field>
                          <FieldLabel htmlFor="pixel-code">
                            <Trans>Pixel Code</Trans>
                          </FieldLabel>
                          <PixelURLInput
                            id="pixel-code"
                            value={field.state.value}
                            onValueChange={field.handleChange}
                            onBlur={field.handleBlur}
                            vendorId={vendorId}
                            disabled={disabled}
                          />
                          <FieldDescription>
                            <Trans>
                              Success! To start tracking impressions, follow these simple{' '}
                              <SupportLink variant="body2" color="aqua" path="PIXEL">
                                instructions
                              </SupportLink>{' '}
                              on how to place it within your digital content.
                            </Trans>
                          </FieldDescription>
                        </field.Field>
                      )}
                    </form.AppField>
                  </div>
                </>
              )
            }
          </form.Subscribe>
        </div>
      </div>
    );
  },
});

export default PixelForm;
